import {EMPTY_STRING, COMMA} from '../../Common/Constants/Separators'
import { RecordingIntegrations } from '../../Common/Constants/Recordings'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { IsVarEmpty, isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { PartitionAdminDTO, TelSysAdminDTO } from '../../../NonReact/Types/Types'
import { PartitionConfigurationForm, PartitionConfigurationRequest } from '../Types/Types'
import {
    PARTITION_SYNC_ENVIRONMENT,
    PARTITION_SYNC_OPTIONS,
    PARTITION_SYNC_TYPE,
    PartitionSyncOption
} from '../Constants/Constants'
import {
    getPartitionModalTelSysLayout,
    PartitionModalSyncLayout,
    PartitionModalTelSysLayout
} from './partitionModalTelSysLayout'
import {
    normalisePartitionTelSysComponentTypeValue,
    shouldShowPartitionTelSysComponentTypeId,
    shouldShowPartitionTelSysComponentTypeOwner
} from './partitionTelSysComponentTypes'

const GMT_TIMEZONE: string = 'GMT'
const DEFAULT_START_OF_DAY: string = '00:00:00'
const DEFAULT_SYNC_TYPE_ONE_TIME_FULL: string = PARTITION_SYNC_TYPE.ONE_TIME_FULL
const DEFAULT_BILLED_MONTHLY: boolean = false
const DEFAULT_COMMS_ENABLED: boolean = true
const DEFAULT_E164_ENABLED: boolean = true
const DEFAULT_TRUNK_CAPACITY: string = '0'

/**
 * Resolves the site settings recording playback value with string-safe fallbacks.
 *
 * @param preferredValue - Primary value (for example partition-level value in edit mode).
 * @param fallbackValue - Parent TelSys default value.
 * @returns Recording integration ID to store in form state.
 */
const resolveRecordingPlaybackValue = (preferredValue: Definable<string>, fallbackValue: Definable<string>): string => {
    if (isStringNotEmpty(preferredValue)) {
        return preferredValue
    }

    if (isStringNotEmpty(fallbackValue)) {
        return fallbackValue
    }

    return RecordingIntegrations.NONE
}

/**
 * True when the current licence supports supervisor-permission syncing.
 *
 * @param licensedFor - Current partition licence.
 * @returns Whether the supervisor-permissions checkbox is visible.
 */
const supportsSupervisorPermissions = (licensedFor: string): boolean =>
    [
        ReportingAccessLevels.ENTERPRISE,
        ReportingAccessLevels.CALL_CENTRE_AGENT,
        ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
    ].includes(licensedFor as ReportingAccessLevels)

/**
 * True when the current sync type exposes the full-sync-only fields.
 *
 * @param syncType - Current synchronisation type value.
 * @returns Whether the full-sync fields are visible.
 */
const isFullSyncType = (syncType: string): boolean =>
    syncType === PARTITION_SYNC_TYPE.FULL || syncType === PARTITION_SYNC_TYPE.ONE_TIME_FULL

/**
 * Service pack synchronisation is only valid for BroadWorks; coerce for other TelSys layouts.
 *
 * @param syncLayout - Layout derived from the parent TelSys type.
 * @param syncType - Raw sync type from API or form.
 * @returns Sync type safe for the given layout.
 */
const normaliseSyncTypeForLayout = (syncLayout: PartitionModalSyncLayout, syncType: string): string => {
    const base: string = isStringNotEmpty(syncType) ? syncType : DEFAULT_SYNC_TYPE_ONE_TIME_FULL

    if (syncLayout !== PartitionModalSyncLayout.BROADWORKS && base === PARTITION_SYNC_TYPE.SERVICE_PACK) return DEFAULT_SYNC_TYPE_ONE_TIME_FULL

    return base
}

/**
 * Parses sync options from API string into form multi-select values.
 *
 * @param syncOptions - API value (comma-separated option names or numeric bitmask).
 * @returns Normalised option-name list for the form.
 */
const mapSyncOptionsFromApi = (syncOptions: Nullable<Definable<string>>): PartitionSyncOption[] => {
    if (!isStringNotEmpty(syncOptions)) {
        return []
    }

    const asNumber: number = Number(syncOptions)
    if (!Number.isNaN(asNumber) && Number.isInteger(asNumber) && asNumber >= 0) {
        return PARTITION_SYNC_OPTIONS.filter((_, index: number): boolean => {
            const bitValue: number = 1 << index

            return (asNumber & bitValue) !== 0
        })
    }

    const requestedOptionNames: string[] = syncOptions
        .split(COMMA)
        .map((candidate: string): string => candidate.trim())
        .filter((candidate: string): boolean => candidate !== EMPTY_STRING)

    return PARTITION_SYNC_OPTIONS.filter((option: PartitionSyncOption): boolean =>
        requestedOptionNames.some(
            (requestedOptionName: string): boolean => requestedOptionName.toLowerCase() === option.toLowerCase()
        )
    )
}

/**
 * Serialises form multi-select values into API SyncOptions format.
 *
 * @param syncOptions - Selected form option names.
 * @returns API value expected by partition add/change.
 */
const mapSyncOptionsToApi = (syncOptions: readonly PartitionSyncOption[]): string =>
    syncOptions
        .filter((syncOption: PartitionSyncOption): boolean => PARTITION_SYNC_OPTIONS.includes(syncOption))
        .join(COMMA)

/**
 * Narrows a raw TelSys type string to {@link TelSysTypes} when it matches a known enum value.
 *
 * @param value - Raw TelSys type from the API.
 * @returns Matching enum value, or `null` when empty or unknown.
 */
const toKnownTelSysType = (value: Definable<string>): Nullable<TelSysTypes> => {
    if (!isStringNotEmpty(value)) {
        return null
    }

    const knownTypes: readonly string[] = Object.values(TelSysTypes) as string[]

    if (!knownTypes.includes(value)) {
        return null
    }

    return value as TelSysTypes
}

/**
 * Builds the servlet-equivalent add-mode defaults derived from the owning TelSys.
 *
 * @param telSys - Parent TelSys payload used by the legacy servlet for add-page defaults.
 * @returns Default form values for a new partition.
 */
const mapPartitionTelSysDefaultsToForm = (telSys: Nullable<TelSysAdminDTO>): PartitionConfigurationForm => {
    const telSysType: Nullable<TelSysTypes> = toKnownTelSysType(telSys?.TelSysType)
    const isWebexCalling: boolean = telSysType === TelSysTypes.WEBEX_CALLING
    const isMsTeams: boolean = telSysType === TelSysTypes.MS_TEAMS
    const isBroadworks: boolean = telSysType === TelSysTypes.BROADSOFT_BROADWORKS
    const telSysLayout: PartitionModalTelSysLayout = getPartitionModalTelSysLayout(telSysType ?? null)
    const licensedForRptUserType: string = isStringNotEmpty(telSys?.LicensedForRptUserType)
        ? telSys.LicensedForRptUserType
        : ReportingAccessLevels.LITE
    let addModeLicensedFor: string = licensedForRptUserType

    if (isWebexCalling || isMsTeams) {
        addModeLicensedFor = ReportingAccessLevels.ENTERPRISE
    } else if (isBroadworks) {
        addModeLicensedFor = ReportingAccessLevels.TWO_THOUSAND
    }

    return {
        general: {
            identifier: EMPTY_STRING,
            description: EMPTY_STRING,
            tags: EMPTY_STRING,
            billingTag: EMPTY_STRING,
            licensedForRptUserType: addModeLicensedFor,
            billedMonthly: isWebexCalling ? true : DEFAULT_BILLED_MONTHLY,
            deleteAcdAgentsOnLicenseDowngrade: false,
            partIDInTelSys: EMPTY_STRING,
            isGlobal: false,
            idOnTelSysOwner: EMPTY_STRING,
            idOnTelSys: EMPTY_STRING,
            telSysComponentType: normalisePartitionTelSysComponentTypeValue(EMPTY_STRING, telSysLayout.syncLayout),
            commsEnabled: DEFAULT_COMMS_ENABLED
        },
        siteSettings: {
            recordingPlayback: resolveRecordingPlaybackValue(telSys?.CallRecIntIDDefault, undefined),
            e164Enabled: telSys?.E164Enabled ?? DEFAULT_E164_ENABLED,
            internalDigitLen: IsVarEmpty(telSys?.InternalDigitLen) ? EMPTY_STRING : String(telSys?.InternalDigitLen),
            countryCode: IsVarEmpty(telSys?.CountryCode) ? EMPTY_STRING : String(telSys?.CountryCode),
            timezone: isStringNotEmpty(telSys?.TimeZone) ? telSys.TimeZone : GMT_TIMEZONE,
            startOfDay: telSys?.StartOfDay ?? DEFAULT_START_OF_DAY
        },
        synchronisation: {
            syncType: DEFAULT_SYNC_TYPE_ONE_TIME_FULL,
            syncEnvironment: PARTITION_SYNC_ENVIRONMENT.NONE,
            securityGroupIDs: EMPTY_STRING,
            syncOptions:
                telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS ? [...PARTITION_SYNC_OPTIONS] : [],
            maintainParity: false,
            forceMonitored: false,
            synchroniseSupervisorPermissions: false,
            syncNow: false,
            photoSyncNow: false
        },
        advanced: {
            trunkCapacity: DEFAULT_TRUNK_CAPACITY,
            trunkCapacityBurst: DEFAULT_TRUNK_CAPACITY,
            ctiLogEnabled: false,
            authUserID1: EMPTY_STRING,
            authPassword1: EMPTY_STRING
        }
    }
}

/**
 * Maps partition API response to form state.
 *
 * @param dto - Partition API data.
 * @param telSys - Parent TelSys payload used for add-mode and invalid-value fallbacks.
 * @returns Form state used by the configuration tabs.
 */
export const mapPartitionDtoToForm = (
    dto: Nullable<PartitionAdminDTO>,
    telSys: Nullable<TelSysAdminDTO> = null
): PartitionConfigurationForm => {
    if (IsVarEmpty(dto)) {
        return mapPartitionTelSysDefaultsToForm(telSys)
    }

    const dtoLicensedFor: Definable<string> = dto?.LicensedForRptUserType
    const telSysLicensedFor: Definable<string> = telSys?.LicensedForRptUserType

    let licensedForRptUserType: string = ReportingAccessLevels.LITE

    if (isStringNotEmpty(dtoLicensedFor)) {
        licensedForRptUserType = dtoLicensedFor
    } else if (isStringNotEmpty(telSysLicensedFor)) {
        licensedForRptUserType = telSysLicensedFor
    }

    let timezone: string = GMT_TIMEZONE

    if (isStringNotEmpty(dto?.TimeZone)) {
        timezone = dto.TimeZone
    } else if (isStringNotEmpty(telSys?.TimeZone)) {
        timezone = telSys.TimeZone
    }

    let internalDigitLen: string = EMPTY_STRING

    if (!IsVarEmpty(dto?.InternalDigitLen)) {
        internalDigitLen = String(dto?.InternalDigitLen)
    } else if (!IsVarEmpty(telSys?.InternalDigitLen)) {
        internalDigitLen = String(telSys?.InternalDigitLen)
    }

    let countryCode: string = EMPTY_STRING

    if (!IsVarEmpty(dto?.CountryCode)) {
        countryCode = String(dto?.CountryCode)
    } else if (!IsVarEmpty(telSys?.CountryCode)) {
        countryCode = String(telSys?.CountryCode)
    }

    const telSysLayout: PartitionModalTelSysLayout = getPartitionModalTelSysLayout(
        toKnownTelSysType(telSys?.TelSysType)
    )

    return {
        general: {
            identifier: dto?.GUID ?? dto?.PartitionGUID ?? EMPTY_STRING,
            description: dto?.Description ?? EMPTY_STRING,
            tags: EMPTY_STRING,
            billingTag: dto?.BillingTag ?? EMPTY_STRING,
            licensedForRptUserType,
            billedMonthly: dto?.BilledMonthly ?? false,
            deleteAcdAgentsOnLicenseDowngrade: false,
            partIDInTelSys: IsVarEmpty(dto?.PartIDInTelSys) ? EMPTY_STRING : String(dto?.PartIDInTelSys),
            isGlobal: dto?.IsGlobal ?? false,
            idOnTelSysOwner: dto?.IDOnTelSysOwner ?? EMPTY_STRING,
            idOnTelSys: dto?.IDOnTelSys ?? EMPTY_STRING,
            telSysComponentType: normalisePartitionTelSysComponentTypeValue(
                dto?.TelSysComponentType ?? EMPTY_STRING,
                telSysLayout.syncLayout
            ),
            commsEnabled: dto?.CommsEnabled ?? true
        },
        siteSettings: {
            recordingPlayback: resolveRecordingPlaybackValue(dto?.CallRecIntIDDefault, telSys?.CallRecIntIDDefault),
            e164Enabled: dto?.E164Enabled ?? telSys?.E164Enabled ?? true,
            internalDigitLen,
            countryCode,
            timezone,
            startOfDay: dto?.StartOfDay ?? telSys?.StartOfDay ?? DEFAULT_START_OF_DAY
        },
        synchronisation: {
            syncType: normaliseSyncTypeForLayout(
                telSysLayout.syncLayout,
                dto?.SyncType ?? DEFAULT_SYNC_TYPE_ONE_TIME_FULL
            ),
            syncEnvironment: dto?.SyncConfigurations?.SyncEnvironment ?? PARTITION_SYNC_ENVIRONMENT.NONE,
            securityGroupIDs: dto?.SyncConfigurations?.SecurityGroups?.GroupIDs?.join(COMMA) ?? EMPTY_STRING,
            syncOptions:
                telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS
                    ? mapSyncOptionsFromApi(dto?.SyncOptions)
                    : [],
            maintainParity: dto?.MaintainParity ?? false,
            forceMonitored: dto?.ForceMonitored ?? false,
            synchroniseSupervisorPermissions: dto?.SynchroniseSupervisorPermissions ?? false,
            syncNow: dto?.SyncNow ?? false,
            photoSyncNow: dto?.PhotoSyncNow ?? false
        },
        advanced: {
            trunkCapacity: IsVarEmpty(dto?.TrunkCapacity) ? DEFAULT_TRUNK_CAPACITY : String(dto?.TrunkCapacity),
            trunkCapacityBurst: IsVarEmpty(dto?.TrunkCapacityBurst)
                ? DEFAULT_TRUNK_CAPACITY
                : String(dto?.TrunkCapacityBurst),
            ctiLogEnabled: dto?.CTILogEnabled ?? false,
            authUserID1: EMPTY_STRING,
            authPassword1: EMPTY_STRING
        }
    }
}

/**
 * Maps form values to partition create/update request payload.
 *
 * @param form - Partition configuration form state.
 * @param telSysID - TelSys scope id.
 * @param telSysLayout - Parent TelSys layout used to match the visible sync fields.
 * @returns Request payload for partition endpoints.
 */
export const mapPartitionFormToRequest = (
    form: PartitionConfigurationForm,
    telSysID: number,
    telSysLayout: PartitionModalTelSysLayout
): PartitionConfigurationRequest => {
    const syncTabHidden: boolean = telSysLayout.syncLayout === PartitionModalSyncLayout.NONE || form.general.isGlobal
    const showComponentType: boolean = !syncTabHidden && telSysLayout.syncLayout !== PartitionModalSyncLayout.TEAMS
    const showIdOnTelSysOwner: boolean =
        !syncTabHidden &&
        telSysLayout.syncLayout !== PartitionModalSyncLayout.M6 &&
        shouldShowPartitionTelSysComponentTypeOwner(telSysLayout.syncLayout, form.general.telSysComponentType)
    const showIdOnTelSys: boolean =
        !syncTabHidden &&
        (telSysLayout.syncLayout === PartitionModalSyncLayout.M6 ||
            shouldShowPartitionTelSysComponentTypeId(telSysLayout.syncLayout, form.general.telSysComponentType))
    const showSyncEnvironment: boolean = !syncTabHidden && telSysLayout.syncLayout === PartitionModalSyncLayout.TEAMS
    const showSyncType: boolean = !syncTabHidden && telSysLayout.syncLayout !== PartitionModalSyncLayout.TEAMS
    const fullSyncSelected: boolean = isFullSyncType(form.synchronisation.syncType)
    const showSecurityGroups: boolean =
        !syncTabHidden &&
        telSysLayout.syncLayout === PartitionModalSyncLayout.TEAMS &&
        form.synchronisation.syncEnvironment === PARTITION_SYNC_ENVIRONMENT.SECURITY_GROUPS
    const showSyncOptions: boolean =
        !syncTabHidden && telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS && fullSyncSelected
    const showMaintainParity: boolean = !syncTabHidden
    const showForceMonitored: boolean =
        !syncTabHidden && telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS && fullSyncSelected
    const showSupervisorPermissions: boolean =
        !syncTabHidden &&
        (telSysLayout.syncLayout === PartitionModalSyncLayout.BROADWORKS ||
            supportsSupervisorPermissions(form.general.licensedForRptUserType))
    const showPhotoSyncNow: boolean = !syncTabHidden && telSysLayout.syncLayout === PartitionModalSyncLayout.TEAMS
    const showSyncNow: boolean = !syncTabHidden
    const syncTypeForRequest: string = normaliseSyncTypeForLayout(
        telSysLayout.syncLayout,
        form.synchronisation.syncType
    )

    return {
        TelSysID: telSysID,
        GUID: form.general.identifier === EMPTY_STRING ? undefined : form.general.identifier,
        Description: form.general.description,
        BillingTag: form.general.billingTag === EMPTY_STRING ? null : form.general.billingTag,
        CommsEnabled: form.general.commsEnabled,
        SyncNow: showSyncNow ? form.synchronisation.syncNow : null,
        PhotoSyncNow: showPhotoSyncNow ? form.synchronisation.photoSyncNow : null,
        E164Enabled: form.siteSettings.e164Enabled,
        PartIDInTelSys:
            form.general.partIDInTelSys === EMPTY_STRING ? undefined : Number.parseInt(form.general.partIDInTelSys, 10),
        TimeZone: form.siteSettings.timezone,
        StartOfDay: form.siteSettings.startOfDay,
        CallRecIntIDDefault:
            form.siteSettings.recordingPlayback === EMPTY_STRING
                ? RecordingIntegrations.NONE
                : form.siteSettings.recordingPlayback,
        InternalDigitLen:
            form.siteSettings.internalDigitLen === EMPTY_STRING
                ? undefined
                : Number.parseInt(form.siteSettings.internalDigitLen, 10),
        CountryCode: form.siteSettings.countryCode,
        MaintainParity: showMaintainParity ? form.synchronisation.maintainParity : null,
        ForceMonitored: showForceMonitored ? form.synchronisation.forceMonitored : null,
        SynchroniseSupervisorPermissions: showSupervisorPermissions
            ? form.synchronisation.synchroniseSupervisorPermissions
            : null,
        CTILogEnabled: form.advanced.ctiLogEnabled,
        BilledMonthly: form.general.billedMonthly,
        LicensedForRptUserType:
            form.general.licensedForRptUserType === EMPTY_STRING ||
            form.general.licensedForRptUserType === ReportingAccessLevels.NONE
                ? EMPTY_STRING
                : form.general.licensedForRptUserType,
        IsGlobal: form.general.isGlobal,
        IDOnTelSysOwner: showIdOnTelSysOwner ? form.general.idOnTelSysOwner : null,
        IDOnTelSys: showIdOnTelSys ? form.general.idOnTelSys : null,
        TelSysComponentType: showComponentType ? form.general.telSysComponentType : null,
        SyncType: showSyncType ? syncTypeForRequest : null,
        SyncOptions: showSyncOptions ? mapSyncOptionsToApi(form.synchronisation.syncOptions) : null,
        DeleteACDAgentsOnLicenseDowngrade: form.general.deleteAcdAgentsOnLicenseDowngrade,
        TrunkCapacity:
            form.advanced.trunkCapacity === EMPTY_STRING ? undefined : Number.parseInt(form.advanced.trunkCapacity, 10),
        TrunkCapacityBurst:
            form.advanced.trunkCapacityBurst === EMPTY_STRING
                ? undefined
                : Number.parseInt(form.advanced.trunkCapacityBurst, 10),
        SyncConfigurations: showSyncEnvironment
            ? {
                  SyncEnvironment: form.synchronisation.syncEnvironment,
                  SecurityGroups: showSecurityGroups
                      ? {
                            GroupIDs: form.synchronisation.securityGroupIDs
                                .split(COMMA)
                                .map((id: string): string => id.trim())
                                .filter((id: string): boolean => id !== EMPTY_STRING),
                            GroupNames: []
                        }
                      : null
              }
            : null
    }
}
